/**
 * Укладка слоя рубежей ИЭЗ на карту MapLibre.
 *
 * Отдельным модулем, потому что карт в кабинете НЕ одна: общий `MapCanvas`
 * («Карты» и «Аналитика») и своя карта раздела «Буи». Вторая копия укладки
 * разъехалась бы с первой при первой же правке — ровно так расходились палитры
 * карты и пороги скорости судна.
 *
 * Что здесь есть и чего нет: этот модуль только КЛАДЁТ готовые фичи. Откуда они
 * берутся (запрос по кадру, дебаунс, пустой ответ) — забота `useEezLayer`, и она
 * тоже одна на всех.
 */
import type maplibregl from 'maplibre-gl'

import {
  EEZ_DASH_ARRAY,
  EEZ_LABEL_MIN_ZOOM,
  decorateEez,
  eezDashLayerId,
  EEZ_DASH_GROUPS,
  eezLayerId,
  eezWidthByPropExpr,
} from './eez'
import { OVERLAY_PREFIX } from './layerOrder'

/** id источника. Один на карту: рубежи — единственный набор, слои читают его
 *  все и различаются только фильтром штриховки. */
export const EEZ_SOURCE_ID = OVERLAY_PREFIX + 'eez-src'

const DASHES = EEZ_DASH_GROUPS

/** Слои снизу вверх: белая подложка, три штриховки, подписи.
 *
 *  Порядок не косметика. Подложка ПОД линиями — белый ореол поверх закрасил бы
 *  их целиком; подписи ВЫШЕ всех — иначе линия перечёркивает собственный ярлык.
 */
export const EEZ_LAYER_IDS: string[] = [
  eezLayerId('casing'),
  ...DASHES.map(eezDashLayerId),
  eezLayerId('label'),
]

/**
 * Положить (или обновить) рубежи на карту.
 *
 * `beforeId` — под какой чужой слой класть. У «Буёв» это символы платформ:
 * рубежи обязаны лежать под ними, иначе линия перечёркивает то, ради чего
 * раздел и открыт. У `MapCanvas` порядок держит своя стопка оверлеев.
 *
 * Повторный вызов ОБНОВЛЯЕТ данные, а не пересоздаёт слои: набор их постоянен
 * (три штриховки), меняется только геометрия кадра, и пересоздание мигало бы на
 * каждый пан.
 */
export function applyEezLayers(
  map: maplibregl.Map,
  data: GeoJSON.FeatureCollection,
  beforeId?: string,
): void {
  // Начертание, цвет и ПОДПИСЬ считаются здесь, а не выражениями MapLibre:
  // подпись зависит от языка кабинета, а язык в стиль карты не приезжает.
  const decorated = decorateEez(data)

  const existing = map.getSource(EEZ_SOURCE_ID) as maplibregl.GeoJSONSource | undefined
  if (existing) {
    existing.setData(decorated)
    return
  }
  map.addSource(EEZ_SOURCE_ID, { type: 'geojson', data: decorated })

  // MapLibre бросает, если ориентира нет. У «Буёв» это слой трека, созданный в
  // обработчике `load`; пересборка стиля способна оставить нас без него, и
  // слой рубежей обязан тогда лечь сверху, а не уронить карту целиком.
  const before = beforeId && map.getLayer(beforeId) ? beforeId : undefined

  // Белая подложка: рубеж ложится и на светлую сушу, и на тёмное поле слоя, и
  // без обводки на одном из двух пропадает.
  map.addLayer({
    id: eezLayerId('casing'),
    type: 'line',
    source: EEZ_SOURCE_ID,
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: {
      'line-color': '#ffffff',
      'line-width': eezWidthByPropExpr(1.4) as maplibregl.ExpressionSpecification,
      'line-opacity': 0.7,
    },
  }, before)

  for (const dash of DASHES) {
    const array = EEZ_DASH_ARRAY[dash]
    map.addLayer({
      id: eezDashLayerId(dash),
      type: 'line',
      source: EEZ_SOURCE_ID,
      filter: ['==', ['get', '_dash'], dash] as maplibregl.FilterSpecification,
      layout: { 'line-cap': array ? 'butt' : 'round', 'line-join': 'round' },
      paint: {
        'line-color': ['get', '_color'] as unknown as maplibregl.ExpressionSpecification,
        'line-width': eezWidthByPropExpr() as maplibregl.ExpressionSpecification,
        ...(array ? { 'line-dasharray': array } : {}),
      },
    }, before)
  }

  map.addLayer({
    id: eezLayerId('label'),
    type: 'symbol',
    source: EEZ_SOURCE_ID,
    // На обзорном зуме ярлыки стран сливаются в кашу — там рубежи различает
    // легенда, а не карта.
    minzoom: EEZ_LABEL_MIN_ZOOM,
    layout: {
      'text-field': ['get', '_label'] as unknown as string,
      // Куски рубежа приходят СШИТЫМИ (ST_LineMerge на сервере), поэтому
      // подпись можно вести вдоль линии. Изотермам этого нельзя: у них
      // геометрия несшитая, и ярлык встал бы в каждую ячейку сетки.
      'symbol-placement': 'line',
      'symbol-spacing': 320,
      'text-size': 11,
      'text-allow-overlap': false,
      'text-padding': 6,
    },
    paint: {
      'text-color': ['get', '_color'] as unknown as maplibregl.ExpressionSpecification,
      'text-halo-color': '#ffffff',
      'text-halo-width': 1.4,
    },
  }, before)
}

/** Снять рубежи. Снятие с чистой карты — обычное дело: тумблер выключают и при
 *  пустом кадре, и до первой загрузки. */
export function removeEezLayers(map: maplibregl.Map): void {
  for (const id of [...EEZ_LAYER_IDS].reverse()) {
    if (map.getLayer(id)) map.removeLayer(id)
  }
  if (map.getSource(EEZ_SOURCE_ID)) map.removeSource(EEZ_SOURCE_ID)
}
